/**
 * App component: wires the sidebar model, session store, and routing
 * into the drawer layout.
 */
import { useEffect, useMemo, useState } from 'preact/hooks';
import { Router, useRoute } from 'wouter-preact';
import { useHashLocation } from 'wouter-preact/use-hash-location';

import { DialogProvider } from './components/Dialog.jsx';
import { ContextMenuHost } from './components/ContextMenu.jsx';
import { SidebarView, createSidebarModel } from './components/Sidebar.jsx';
import { useSessionStore } from './features/Session.jsx';
import { LandingPage, SessionPage } from './features/Session.jsx';
import { SharePage } from './features/Share.jsx';
import { FSPage } from './features/FS.jsx';
import { SettingsPage } from './features/Settings.jsx';
import { CronPage } from './features/Cron.jsx';
import { ConnectorPage } from './features/Connector.jsx';
import { getAuthStatus, setOIDCEnabled, login } from './lib/auth.js';

// AuthGate fetches the server auth state on mount and, when OIDC is enabled
// but the browser has no valid session, replaces the app with a sign-in screen.
function AuthGate({ children }) {
  const [state, setState] = useState('checking'); // checking | ok | signin
  useEffect(() => {
    let alive = true;
    getAuthStatus()
      .then((s) => {
        if (!alive) return;
        setOIDCEnabled(!!s && !!s.oidc_enabled);
        setState(!!s && s.oidc_enabled && !s.authenticated ? 'signin' : 'ok');
      })
      .catch(() => {
        if (alive) setState('ok');
      });
    return () => {
      alive = false;
    };
  }, []);

  if (state === 'checking') {
    return (
      <div className="h-screen grid place-items-center bg-base-200">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }
  if (state === 'signin') {
    return (
      <div className="h-screen grid place-items-center bg-base-200">
        <div className="card bg-base-100 border border-base-300 shadow-xl max-w-sm w-full mx-4">
          <div className="card-body items-center text-center">
            <h1 className="card-title">Talos</h1>
            <p className="text-sm text-base-content/70">
              You need to sign in to continue.
            </p>
            <div className="card-actions mt-2 w-full">
              <button className="btn btn-primary w-full" onClick={login}>
                Sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return children;
}

function AppContent() {
  const sidebar = useMemo(() => createSidebarModel(), []);

  // Register static sidebar content.
  useEffect(() => {
    sidebar.addAction({
      id: 'new-session',
      label: 'New Session',
      icon: 'plus',
      onClick: () => {
        window.location.hash = '#/';
      },
    });
    sidebar.addAction({
      id: 'fs',
      label: 'Files',
      icon: 'folder',
      route: '/fs',
    });
    sidebar.addAction({
      id: 'cron',
      label: 'Scheduled',
      icon: 'clock',
      route: '/cron',
    });
    sidebar.addAction({
      id: 'connectors',
      label: 'Connectors',
      icon: 'bolt',
      route: '/connectors',
    });
    sidebar.registerSection({ id: 'sessions', label: 'Sessions', icon: 'chat' });
  }, [sidebar]);

  const { sessions } = useSessionStore(sidebar);

  return (
    <div className="drawer lg:drawer-open h-full min-h-0">
      <input id="sidebar-toggle" type="checkbox" className="drawer-toggle" />
      <div className="drawer-content flex flex-col min-h-0">
        {/* Mobile header with sidebar toggle */}
        <MobileHeader />
        {/* Main content */}
        <main id="content-area" className="flex-1 flex flex-col min-h-0 relative">
          {sessions && (
            <Routes sessions={sessions} sidebar={sidebar} />
          )}
        </main>
      </div>
      {/* Sidebar */}
      <aside className="drawer-side z-40 lg:z-auto">
        <label htmlFor="sidebar-toggle" aria-label="close sidebar" className="drawer-overlay lg:hidden" />
        <SidebarView model={sidebar} />
      </aside>
    </div>
  );
}

function MobileHeader() {
  return (
    <header className="sticky top-0 z-30 flex items-center gap-3 bg-base-100/90 backdrop-blur border-b border-base-300 px-4 py-2 lg:hidden">
      <label htmlFor="sidebar-toggle" className="btn btn-ghost btn-sm btn-square drawer-button">
        <svg viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5" aria-hidden="true">
          <path fill-rule="evenodd" d="M2 4.75A.75.75 0 0 1 2.75 4h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 4.75Zm0 5a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 9.75Zm0 5a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 14.75Z" clip-rule="evenodd" />
        </svg>
      </label>
      <span className="font-semibold text-lg">Talos</span>
    </header>
  );
}

function Routes({ sessions, sidebar }) {
  const [, params] = useRoute('/s/:id');
  const [, fsMatch] = useRoute('/fs');
  const [, cronMatch] = useRoute('/cron');
  const [, connectorsMatch] = useRoute('/connectors');
  const [, settingsMatch] = useRoute('/settings');
  const [, shareMatch] = useRoute('/share');
  const [, landingMatch] = useRoute('/');

  if (shareMatch) return <SharePage />;
  if (landingMatch) return <LandingPage sessions={sessions} addSessionEntry={sidebar.addEntry.bind(sidebar)} />;
  if (fsMatch) return <FSPage />;
  if (cronMatch) return <CronPage sessions={sessions} />;
  if (connectorsMatch) return <ConnectorPage />;
  if (settingsMatch) return <SettingsPage />;
  if (params) return <SessionPage id={params.id} sessions={sessions} />;
  return null;
}

export function App() {
  return (
    <DialogProvider>
      <AuthGate>
        <Router hook={useHashLocation}>
          <AppContent />
        </Router>
        <ContextMenuHost />
      </AuthGate>
    </DialogProvider>
  );
}